import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parse } from "date-fns";
import axios from "../../services/api";
import Select from "react-select";
import "../../styles/app.css";

const RequisitionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // present only in edit mode
  const userId = localStorage.getItem("user_id") || "";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(!!id);

  const [formData, setFormData] = useState({
    user_id: userId,
    expected_date: new Date(new Date().setDate(new Date().getDate() + 9)),
    priority: "Medium",
    items: [],
    req_description: "",
  });

  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchItems();
  }, []);

  // In edit mode: fetch existing requisition once items are loaded
  useEffect(() => {
    if (id && availableItems.length > 0) {
      fetchRequisitionForEdit();
    }
  }, [id, availableItems]);

  const fetchItems = async () => {
    try {
      const response = await axios.get("/items/dropdown");
      const raw = Array.isArray(response.data) ? response.data : (response.data.items || []);
      setAvailableItems(raw.filter(item => item.status !== 0));
    } catch (error) {
      console.error("Error fetching items:", error);
      setAvailableItems([]);
    }
  };

  const fetchRequisitionForEdit = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`/requisitions/${id}`);
      const { requisition, items } = res.data;

      // Guard: only allow edit if Pending
      if (requisition.status !== "Pending") {
        alert("Only Pending requisitions can be edited.");
        navigate("/dashboard/requisition-list");
        return;
      }

      // Parse date from API (yyyy-MM-dd or dd/MM/yyyy)
      let parsedDate;
      try {
        parsedDate = requisition.expected_date.includes("-")
          ? new Date(requisition.expected_date)
          : parse(requisition.expected_date, "dd/MM/yyyy", new Date());
      } catch {
        parsedDate = new Date(new Date().setDate(new Date().getDate() + 9));
      }

      setFormData({
        user_id: userId,
        expected_date: parsedDate,
        priority: requisition.priority || "Medium",
        req_description: requisition.req_description || "",
        items: items.map(i => ({
          id: i.item_id,
          name: i.item_name,
          quantity: Number(i.requested_quantity) || 0,
        })),
      });
    } catch (error) {
      console.error("Error fetching requisition for edit:", error);
      alert("Failed to load requisition.");
      navigate("/dashboard/requisition-list");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, expected_date: date });
  };

  const addItem = () => {
    if (!selectedItem || quantity <= 0) {
      alert("Please select an item and enter a valid quantity.");
      return;
    }
    const selectedItemObj = availableItems.find((item) => item.id === selectedItem.value);
    if (!selectedItemObj) {
      alert("Selected item not found.");
      return;
    }
    setFormData((prev) => {
      const existingIndex = prev.items.findIndex((item) => item.id === selectedItemObj.id);
      if (existingIndex !== -1) {
        const updated = [...prev.items];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: (Number(updated[existingIndex].quantity) || 0) + (Number(quantity) || 0),
        };
        return { ...prev, items: updated };
      }
      return {
        ...prev,
        items: [...prev.items, { id: selectedItemObj.id, name: selectedItemObj.name, quantity: parseInt(quantity) }],
      };
    });
    setSelectedItem(null);
    setQuantity(1);
  };

  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      alert("Please add at least one item.");
      return;
    }
    setIsSubmitting(true);
    try {
      const formattedDate = format(formData.expected_date, "dd/MM/yyyy");
      const payload = {
        user_id: formData.user_id,
        expected_date: formattedDate,
        priority: formData.priority,
        req_description: formData.req_description,
        items: formData.items,
      };

      if (id) {
        // Edit mode — PUT
        await axios.put(`/requisitions/${id}`, payload);
        alert("Requisition updated successfully!");
      } else {
        // Create mode — POST
        await axios.post("/requisitions", payload);
        alert("Requisition Created!");
      }
      navigate("/dashboard/requisition-list");
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      console.error("Error saving requisition:", msg);
      alert(`Failed to ${id ? "update" : "create"} requisition: ${msg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2">Loading requisition...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="ims-page-header">
        <div>
          <h4>{id ? "Edit Requisition" : "Create Requisition"}</h4>
          <p>{id ? "Update items or details before approval" : "Raise a new item requisition"}</p>
        </div>
        <button type="button" className="btn btn-sm btn-secondary"
          onClick={() => navigate("/dashboard/requisition-list")}>
          <i className="fas fa-arrow-left mr-1"></i> Back
        </button>
      </div>
      <form onSubmit={handleSubmit} className="card shadow-sm p-4" style={{ border: "none", borderRadius: "12px" }}>
        <input type="hidden" name="user_id" value={formData.user_id} />

        <div className="row">
          <div className="col-md-3 mb-3">
            <label>Expected Date:</label>
            <DatePicker
              selected={formData.expected_date}
              onChange={handleDateChange}
              dateFormat="dd/MM/yyyy"
              className="form-control datepicker-block"
              minDate={new Date(new Date().setDate(new Date().getDate() + 9))}
              required
            />
          </div>

          <div className="col-md-3 mb-3">
            <label>Priority:</label>
            <select
              name="priority"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="form-control"
            >
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>

          <div className="col-md-6 mb-3">
            <label>Purpose:</label>
            <input
              type="text"
              name="req_description"
              className="form-control"
              value={formData.req_description}
              onChange={(e) => setFormData({ ...formData, req_description: e.target.value })}
            />
          </div>
        </div>

        <h5 className="mt-2 mb-3" style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>
          <i className="fas fa-plus-circle mr-2 text-primary"></i>Add Items
        </h5>
        <div className="row mb-3">
          <div className="col-md-6">
            <Select
              options={availableItems.map((item) => ({ value: item.id, label: item.name }))}
              onChange={setSelectedItem}
              value={selectedItem}
              placeholder="Search & Select Item..."
              isSearchable
            />
          </div>
          <div className="col-md-3">
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              min="1"
              className="form-control"
            />
          </div>
          <div className="col-md-3">
            <button type="button" onClick={addItem} className="btn btn-primary w-100">
              Add
            </button>
          </div>
        </div>

        {formData.items.length > 0 && (
          <div className="table-responsive">
            <table className="ims-table">
              <thead>
                <tr>
                  <th>Item Name</th>
                  <th>Quantity</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {formData.items.map((item, index) => (
                  <tr key={index}>
                    <td>{availableItems.find((i) => i.id === item.id)?.name || item.name}</td>
                    <td>{item.quantity}</td>
                    <td>
                      <button type="button" onClick={() => removeItem(index)} className="btn btn-danger btn-sm">
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="d-flex gap-2 mt-3">
          <button type="submit" className="btn btn-success" style={{ width: "120px" }} disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : id ? "Update" : "Create"}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate("/dashboard/requisition-list")}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default RequisitionForm;
